import { useRef, useCallback } from 'react';

const NP_API = 'https://api.novaposhta.ua/v2.0/json/';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DEBOUNCE_MS = 450;

function cacheKey(...parts) {
    return 'np__' + parts.map(p => String(p).toLowerCase().trim()).join('__');
}

function readCache(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < CACHE_TTL) return data;
        localStorage.removeItem(key);
    } catch {
        // ignore corrupted cache
    }
    return null;
}

function writeCache(key, data) {
    try {
        localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch {
        // ignore storage errors (quota, private mode)
    }
}

async function npRequest(modelName, calledMethod, methodProperties) {
    const res = await fetch(NP_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelName, calledMethod, methodProperties }),
    });
    if (!res.ok) throw new Error(`Nova Poshta API error: ${res.status}`);
    const json = await res.json();
    if (!json.success) throw new Error(json.errors?.[0] || 'Nova Poshta API error');
    return json.data;
}

function useDebounced(fn) {
    const timerRef = useRef(null);
    return useCallback(
        (...args) => {
            clearTimeout(timerRef.current);
            return new Promise((resolve, reject) => {
                timerRef.current = setTimeout(async () => {
                    try {
                        resolve(await fn(...args));
                    } catch (e) {
                        reject(e);
                    }
                }, DEBOUNCE_MS);
            });
        },
        [fn]
    );
}

export function useNovaPoshtaDelivery() {
    const searchCitiesRaw = useCallback(async (query) => {
        if (!query || query.trim().length < 2) return [];
        const key = cacheKey('getCities', query);
        const cached = readCache(key);
        if (cached) return cached;

        const data = await npRequest('Address', 'getCities', {
            FindByString: query.trim(),
            Limit: '20',
        });
        writeCache(key, data);
        return data;
    }, []);

    const searchWarehousesRaw = useCallback(async (cityRef, typeRef) => {
        if (!cityRef) return [];
        const key = cacheKey('getWarehouses', cityRef, typeRef || '');
        const cached = readCache(key);
        if (cached) return cached;

        const props = { CityRef: cityRef, Limit: '200', Page: '1' };
        if (typeRef) props.TypeOfWarehouseRef = typeRef;

        const data = await npRequest('AddressGeneral', 'getWarehouses', props);
        writeCache(key, data);
        return data;
    }, []);

    const searchStreetsRaw = useCallback(async (cityRef, query) => {
        if (!cityRef || !query || query.trim().length < 2) return [];
        const key = cacheKey('getStreet', cityRef, query);
        const cached = readCache(key);
        if (cached) return cached;

        const data = await npRequest('Address', 'getStreet', {
            CityRef: cityRef,
            FindByString: query.trim(),
            Limit: '20',
        });
        writeCache(key, data);
        return data;
    }, []);

    const searchCities = useDebounced(searchCitiesRaw);
    const searchWarehouses = useDebounced(searchWarehousesRaw);
    const searchStreets = useDebounced(searchStreetsRaw);

    return { searchCities, searchWarehouses, searchStreets };
}
